import { ReactNode, SyntheticEvent, useMemo, useState } from "react";
import {
  Box,
  Button,
  IconButton,
  SxProps,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Theme,
  Tooltip,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { Add, Article, Feedback, Info, OpenInBrowser, Visibility, VisibilityOff } from "@mui/icons-material";
import SkeletonRow from "components/SkeletonRow";
import { useAppSelector } from "redux/hooks";
import IntakeRow from "routes/lenses/lens/case/intakes/IntakeRow";
import { TableColumns } from "routes/lenses/lens/case/intakes/TableColumns";
import RequestStatus from "redux/slices/RequestStatus";
import { Case, CaseInfo, DocumentIntake, DocumentStatus } from "generated/graphql";
import { useNavigate } from "react-router-dom";
import { selectVisibleDocumentPropertiesThatHaveValues } from "redux/slices/clientLens";
import { selectCaseClaimantName, selectIntakes, selectIntakesStatus } from "redux/slices/case";
import { Uploader } from "shared/multipart-upload/Uploader";
import UploaderClassImpl from "shared/UploaderClassImpl";
import { AppRoutes } from "AppRoutes";
import theme from "theme";
import { P } from "theme/typography";
import DocumentEventsConsumer from "routes/lenses/lens/case/intakes/DocumentEventsConsumer";
import { HeaderCard, HeaderCardTitle } from "components/HeaderCard";
import { SBRUploadDialog } from "shared/multipart-upload/SBRUploadDialog";
import { useCase } from "helpers/hooks/useCase";

function CaseIntakes({
  intakes,
  caseId,
  lensId,
  showImport,
}: {
  intakes: DocumentIntake[];
  caseId: number;
  lensId: number;
  showImport: (open: boolean) => void;
}) {
  const [showProperties, setShowProperties] = useState(false);
  const caseIntakesStatus = useAppSelector(selectIntakesStatus);
  const visibleDocumentProperties = useAppSelector(selectVisibleDocumentPropertiesThatHaveValues);
  const columns: TableColumns = {
    name: true,
    uploadDate: true,
    status: true,
  };

  return (
    <div className="flex flex-col overflow-hidden min-h-[150px]">
      <div className="flex p-2 items-center gap-2 justify-end">
        {visibleDocumentProperties.length > 0 && (
          <Button
            onClick={() => {
              setShowProperties(!showProperties);
            }}
            endIcon={showProperties ? <Visibility /> : <VisibilityOff />}>
            {showProperties ? `Hide ` : `Show ${visibleDocumentProperties.length}`} Properties
          </Button>
        )}
      </div>
      {intakes.length > 0 ? (
        <TableContainer
          sx={{
            flex: 1,
          }}>
          <Table
            stickyHeader
            sx={{
              tableLayout: {
                overflow: "scroll",
                xs: "fixed",
                sm: "auto",
                "& tr > *:first-of-type": {
                  position: "sticky",
                  left: 0,
                },
              },
            }}>
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{
                    zIndex: 10,
                  }}>
                  Name
                </TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Status</TableCell>
                {showProperties &&
                  visibleDocumentProperties.map((property) => (
                    <TableCell
                      key={property.key}
                      sx={{
                        borderBottom: `2px solid ${theme.palette.primary.main}`,
                      }}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          whiteSpace: "nowrap",
                        }}>
                        {property.displayName}
                        {property.preventsLensHits && (
                          <Tooltip arrow title="Prevents lens hits">
                            <Info sx={{ ml: 1 }} fontSize="small" color="secondary" />
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {caseIntakesStatus === RequestStatus.PENDING &&
                Array(5)
                  .fill(0)
                  .map((_item, idx) => <SkeletonRow key={idx} columnNum={3} />)}
              {intakes.map((intake, idx) => (
                <IntakeRow
                  actionItems={[]}
                  columns={columns}
                  intake={intake}
                  key={idx}
                  caseId={caseId}
                  lensId={lensId}
                  documentProperties={visibleDocumentProperties}
                  showDocumentProperties={showProperties}
                  users={[]}
                  collectUsers={[]}
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            height: "100%",
            gap: 1,
          }}>
          <P>No Documents to Review</P>
          <Button size="small" variant="outlined" onClick={() => showImport(true)}>
            Import Document
          </Button>
        </Box>
      )}
    </div>
  );
}

interface CaseIntakesOverviewProps {
  lensId: number;
  caseId: number;
  lensCase: Case;
  caseInfo?: CaseInfo;
}

const ValueSX: SxProps<Theme> = {
  border: `2px solid ${theme.palette.grey[100]}`,
  borderRadius: `${theme.shape.borderRadius}px`,
  backgroundColor: theme.palette.grey[100],
  p: 0.5,
  ml: 1,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

function TableMetric(props: { title: string; children: ReactNode }) {
  const { title, children } = props;
  return (
    <div className="flex items-center">
      <p className="text-xs font-bold">{title}</p>
      <Box sx={ValueSX}>
        <p className="text-xs font-semibold">{children}</p>
      </Box>
    </div>
  );
}

export default function SBRCaseIntakesOverview(props: CaseIntakesOverviewProps) {
  const { lensId, caseId, lensCase } = props;
  const intakes = useAppSelector(selectIntakes);
  const claimantName = useAppSelector(selectCaseClaimantName);
  const [, setUploads] = useState<Uploader[]>([]);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.only("xs"));
  const [addIntakesOpen, setAddIntakesOpen] = useState(false);
  const navigate = useNavigate();
  const pendingReview = useMemo(
    () => intakes.filter((i) => i.status === DocumentStatus.PendingReview).length,
    [intakes]
  );
  const openDiscover = (query: string) => {
    const url = AppRoutes.discoverLink(lensId, caseId, query);
    navigate(url);
  };
  const caseHook = useCase(caseId);

  return (
    <DocumentEventsConsumer caseId={caseId}>
      <HeaderCard
        title={
          <HeaderCardTitle className="flex items-center">
            <p className="text-sm font-semibold">
              Documents{intakes.length > 0 ? ` (${intakes.length.toLocaleString()})` : ""}
            </p>
            <div className="grow" />
            {!isMobile && (
              <div className="flex gap-2">
                <Tooltip title={intakes.length === 0 ? "No documents to review" : "Review documents"} arrow>
                  <Box component="span" sx={{ mr: 1 }}>
                    <IconButton
                      disabled={intakes.length === 0}
                      onClick={() => {
                        if (intakes[0]) navigate("documents/" + intakes[0].id);
                      }}
                      size="small">
                      <Article />
                    </IconButton>
                  </Box>
                </Tooltip>

                <Tooltip title="Inspect read quality issues" arrow>
                  <IconButton onClick={() => navigate("read-quality")} size="small">
                    <Feedback />
                  </IconButton>
                </Tooltip>

                <Tooltip title="View documents" arrow>
                  <IconButton
                    onClick={() => {
                      navigate("documents");
                    }}
                    size="small">
                    <OpenInBrowser />
                  </IconButton>
                </Tooltip>

                <Tooltip title="Import a new document" arrow>
                  <IconButton onClick={() => setAddIntakesOpen(true)} size="small">
                    <Add />
                  </IconButton>
                </Tooltip>
              </div>
            )}
          </HeaderCardTitle>
        }>
        <div className="p-4 flex flex-col">
          <div className="flex gap-2">
            <TableMetric title="Pages">{lensCase.pageCount.toLocaleString()}</TableMetric>
            <TableMetric title="Reviewed">
              {(intakes.length - pendingReview).toLocaleString()} / {intakes.length.toLocaleString()}
            </TableMetric>
            <div className="grow" />
            <form
              onSubmit={(event: SyntheticEvent) => {
                event.preventDefault();
                const target = event.target as typeof event.target & {
                  discover: { value: string };
                };
                openDiscover(target.discover.value);
              }}
              style={{
                width: "250px",
              }}>
              <TextField
                disabled={lensCase.reviewStatus === "no_documents"}
                id="discover"
                label="Search Document Content"
                fullWidth
              />
            </form>
          </div>
          <CaseIntakes intakes={intakes} caseId={caseId} lensId={lensId} showImport={setAddIntakesOpen} />

          {addIntakesOpen && (
            <SBRUploadDialog
              open={addIntakesOpen}
              close={() => {
                setAddIntakesOpen(false);
                caseHook.onDocumentsUpload();
              }}
              caseId={caseId}
              claimantName={claimantName}
              onUnAuthorized={() => navigate("/logout")}
              setUploads={setUploads}
              UploaderClazz={UploaderClassImpl}
            />
          )}
        </div>
      </HeaderCard>
    </DocumentEventsConsumer>
  );
}
